import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateOfficeMembershipServiceSchema } from '../../utils/seo';

export default function OfficeMembershipPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Page load animation
    setIsVisible(true);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);

    // Intersection Observer for scroll animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    // Observe all animatable elements
    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observerRef.current?.disconnect();
    };
  }, []);

  const handleFormReset = () => {
    setFormState('idle');
    const form = document.getElementById('inquiry-form') as HTMLFormElement;
    if (form) {
      form.reset();
      const counter = document.getElementById('char-counter');
      if (counter) {
        counter.textContent = '0/500 characters';
      }
    }
  };

  useSEO({
    title: 'Office Membership Plans | Private Offices in Oklahoma City — 405-Works',
    description: 'Explore flexible office membership options at 405-Works OKC. Private offices from $300/month, office groups from $1,400/month. All-inclusive amenities at 2520 W. I-44 Service Road.',
    keywords: 'office membership OKC, private office rental Oklahoma City, coworking membership, flexible office space, private office groups, conference room rental OKC',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://405-works.com'}/office-membership`,
    ogImage: 'https://i.imgur.com/k5wqb8a.jpeg',
    schema: [
      generateWebPageSchema(
        'Office Membership Plans | 405-Works Oklahoma City',
        'Flexible office membership options including private offices and office groups with all-inclusive amenities in Oklahoma City.',
        '/office-membership'
      ),
      generateOfficeMembershipServiceSchema(),
    ],
  });

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        .page-enter {
          opacity: 0;
        }

        .page-enter-active {
          opacity: 1;
          transition: opacity 300ms ease-out;
        }

        .animate-on-scroll {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }

        .animate-on-scroll.animate-in {
          opacity: 1;
          transform: translateY(0);
        }

        .stagger-1 { transition-delay: 0ms; }
        .stagger-2 { transition-delay: 100ms; }
        .stagger-3 { transition-delay: 200ms; }
        .stagger-4 { transition-delay: 300ms; }

        .card-hover {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .card-hover:hover {
          transform: translateY(-4px);
        }

        .button-hover {
          transition: transform 0.2s ease, opacity 0.2s ease, background-color 0.3s ease, box-shadow 0.3s ease;
        }

        .button-hover:hover {
          transform: scale(1.02);
        }

        @media (max-width: 768px) {
          .animate-on-scroll {
            transform: translateY(15px);
          }
        }
      `}</style>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-[1240px] mx-auto px-5 md:px-10">
          <div className="flex items-center justify-between h-20 md:h-24">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/a4fec89bcc5ba166ddea2bf47ff2dc5f/e977f47ab61e1eb868be39c5ee14b384.webp"
                alt="405-Works"
                className="h-8 md:h-10 lg:h-12 w-auto"
              />
            </Link>
            <div className="flex items-center gap-4 md:gap-6 lg:gap-10">
              <Link to="/office-membership" className={`hidden md:block text-sm md:text-base font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-[#0A7C7C]' : 'text-white hover:text-[#FFB400]'}`}>
                Co-Working Spaces
              </Link>
              <Link to="/#inquire" className={`text-sm md:text-base font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-[#0A7C7C]' : 'text-white hover:text-[#FFB400]'}`}>
                Contact
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Video Background */}
      <section className="relative min-h-[400px] md:min-h-[500px] lg:h-[650px] flex flex-col justify-end overflow-hidden pt-20 md:pt-0">
        {/* Background Image */}
        <div className="absolute inset-0 z-0 bg-[#0A7C7C]">
          <div className="w-full h-full">
            <img
              src="https://public.readdy.ai/ai/img_res/abff2a67173fd433ffdd76ca3519f15c.jpg"
              alt="405-Works Office Space"
              className={`w-full h-full object-cover object-center transition-opacity duration-150 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              onLoad={() => setImageLoaded(true)}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        {/* Hero Content */}
        <div className={`relative z-10 flex flex-col items-center justify-center text-center px-5 pb-12 md:pb-16 lg:pb-20 ${isVisible ? 'page-enter-active' : 'page-enter'}`}>
          <div className="inline-block px-4 md:px-5 lg:px-6 py-2 mb-4 md:mb-5 lg:mb-6 bg-[#0A7C7C]/20 backdrop-blur-sm rounded-full border border-white/30 stagger-1" style={{ transitionDelay: '150ms', opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease-out, transform 0.6s ease-out' }}>
            <span className="text-white text-xs font-semibold tracking-wider uppercase">Oklahoma City</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 md:mb-5 lg:mb-6 tracking-tight leading-[1.15] stagger-2" style={{ transitionDelay: '250ms', opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease-out, transform 0.6s ease-out' }}>
            Office Membership
          </h1>
          
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-6 md:mb-8 lg:mb-10 max-w-[680px] font-light leading-[1.4] stagger-3" style={{ transitionDelay: '350ms', opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease-out, transform 0.6s ease-out' }}>
            Private workspaces with all-inclusive amenities in Oklahoma City.
          </p>
          
          <a
            href="#inquire"
            className="inline-flex items-center gap-2 md:gap-3 px-6 md:px-8 py-3 md:py-4 bg-[#0A7C7C] text-white rounded-full font-semibold text-sm md:text-base lg:text-lg hover:bg-[#0D9999] whitespace-nowrap cursor-pointer button-hover stagger-4"
            style={{ transitionDelay: '450ms', opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease-out, transform 0.6s ease-out' }}
          >
            Inquire Now
            <i className="ri-arrow-right-line text-base md:text-lg lg:text-xl"></i>
          </a>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce hidden md:block">
          <i className="ri-arrow-down-line text-white text-2xl"></i>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-12 md:py-16 lg:py-24 xl:py-32 bg-[#F5F7F8]">
        <div className="max-w-[1120px] mx-auto px-5 md:px-10">
          <div className="flex flex-col lg:flex-row lg:items-start gap-6 md:gap-8 lg:gap-12">
            <div className="lg:w-2/5">
              <span className="text-[#0A7C7C] text-xs md:text-sm font-bold tracking-widest uppercase animate-on-scroll stagger-1">Why 405-Works</span>
            </div>
            <div className="lg:w-3/5">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-4 md:mb-5 lg:mb-6 leading-[1.2] animate-on-scroll stagger-2">
                Find Your Perfect Space
              </h2>
              <p className="text-sm md:text-base lg:text-lg text-gray-600 leading-[1.65] animate-on-scroll stagger-3">
                At 405-Works, we offer a variety of private and semi-private workspaces to fit businesses at every stage — from solo entrepreneurs to growing teams, to support the level of privacy and space your work requires. A desk is included with all spaces.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Private Suites Section with Image */}
      <section className="py-12 md:py-16 lg:py-24 xl:py-28 bg-white">
        <div className="max-w-[1120px] mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center mb-12 md:mb-16 lg:mb-20">
            <div className="animate-on-scroll stagger-1">
              <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-lg bg-gray-100 card-hover w-full aspect-[4/3]">
                <img
                  src="https://i.imgur.com/k5wqb8a.jpeg"
                  alt="Private Suites at 405-Works"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div>
              <span className="text-[#0A7C7C] text-xs md:text-sm font-bold tracking-widest uppercase mb-2 md:mb-3 block animate-on-scroll stagger-1">
                Downstairs
              </span>
              <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#1A1A1A] mb-4 md:mb-5 lg:mb-6 leading-[1.2] animate-on-scroll stagger-2">Private Office Groups</h3>
              
              <ul className="space-y-2 md:space-y-3 lg:space-y-4 mb-5 md:mb-6 lg:mb-8 animate-on-scroll stagger-3">
                <li className="flex items-start gap-2 md:gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0A7C7C] text-lg md:text-xl mt-0.5 flex-shrink-0"></i>
                  <span className="text-gray-700 text-xs md:text-sm lg:text-base leading-[1.65]">580sf (2 offices + reception area)</span>
                </li>
                <li className="flex items-start gap-2 md:gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0A7C7C] text-lg md:text-xl mt-0.5 flex-shrink-0"></i>
                  <span className="text-gray-700 text-xs md:text-sm lg:text-base leading-[1.65]">536sf (4 offices)</span>
                </li>
                <li className="flex items-start gap-2 md:gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0A7C7C] text-lg md:text-xl mt-0.5 flex-shrink-0"></i>
                  <span className="text-gray-700 text-xs md:text-sm lg:text-base leading-[1.65]">1231sf (4 offices + conference area)</span>
                </li>
                <li className="flex items-start gap-2 md:gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0A7C7C] text-lg md:text-xl mt-0.5 flex-shrink-0"></i>
                  <span className="text-gray-700 text-xs md:text-sm lg:text-base leading-[1.65]">Conference room rentals included</span>
                </li>
              </ul>

              <div className="space-y-2 mb-5 md:mb-6 lg:mb-8 animate-on-scroll stagger-4">
                <p className="text-lg md:text-xl lg:text-2xl font-bold text-[#1A1A1A]"><strong>Starting as low as $1400/month</strong></p>
              </div>

              <p className="text-gray-600 text-xs md:text-sm lg:text-base mb-4 md:mb-5 lg:mb-6 leading-[1.65] animate-on-scroll stagger-4">Spacious, professional, and ideal for growing teams</p>

              <a
                href="#inquire"
                className="inline-flex items-center gap-2 md:gap-3 px-6 md:px-8 py-3 md:py-4 bg-[#0A7C7C] text-white rounded-full font-semibold text-sm md:text-base hover:bg-[#0D9999] whitespace-nowrap cursor-pointer button-hover animate-on-scroll stagger-4"
              >
                Inquire Now
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>

          {/* Private Offices Section with Image */}
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <span className="text-[#0A7C7C] text-xs md:text-sm font-bold tracking-widest uppercase mb-2 md:mb-3 block animate-on-scroll stagger-1">
                Upstairs Options Available
              </span>
              <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#1A1A1A] mb-4 md:mb-5 lg:mb-6 leading-[1.2] animate-on-scroll stagger-2">Private Offices</h3>
              
              <ul className="space-y-2 md:space-y-3 lg:space-y-4 mb-5 md:mb-6 lg:mb-8 animate-on-scroll stagger-3">
                <li className="flex items-start gap-2 md:gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0A7C7C] text-lg md:text-xl mt-0.5 flex-shrink-0"></i>
                  <span className="text-gray-700 text-xs md:text-sm lg:text-base leading-[1.65]">Ranging from 71sf – 202sf</span>
                </li>
                <li className="flex items-start gap-2 md:gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0A7C7C] text-lg md:text-xl mt-0.5 flex-shrink-0"></i>
                  <span className="text-gray-700 text-xs md:text-sm lg:text-base leading-[1.65]">Conference room rentals included</span>
                </li>
              </ul>

              <div className="space-y-2 mb-5 md:mb-6 lg:mb-8 animate-on-scroll stagger-4">
                <p className="text-lg md:text-xl lg:text-2xl font-bold text-[#1A1A1A]"><strong>Starting as low as $300/month</strong></p>
              </div>

              <p className="text-gray-600 text-xs md:text-sm lg:text-base mb-4 md:mb-5 lg:mb-6 leading-[1.65] animate-on-scroll stagger-4">Perfect for focused, individual work</p>

              <a
                href="#inquire"
                className="inline-flex items-center gap-2 md:gap-3 px-6 md:px-8 py-3 md:py-4 bg-[#0A7C7C] text-white rounded-full font-semibold text-sm md:text-base hover:bg-[#0D9999] whitespace-nowrap cursor-pointer button-hover animate-on-scroll stagger-4"
              >
                Inquire Now
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="order-1 lg:order-2 animate-on-scroll stagger-1">
              <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-lg bg-gray-100 card-hover w-full aspect-[4/3]">
                <img
                  src="https://i.imgur.com/m9zc3wP.jpeg"
                  alt="Private Offices at 405-Works"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-12 md:py-16 lg:py-24 xl:py-28 bg-white">
        <div className="max-w-[1120px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 lg:gap-6">
            <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-lg bg-gray-100 card-hover animate-on-scroll stagger-1">
              <div className="w-full aspect-[3/4]">
                <img
                  src="https://i.imgur.com/k5wqb8a.jpeg"
                  alt="405-Works Office Space"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-lg bg-gray-100 card-hover animate-on-scroll stagger-2">
              <div className="w-full aspect-[3/4]">
                <img
                  src="https://i.imgur.com/m9zc3wP.jpeg"
                  alt="405-Works Office Interior"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-lg bg-gray-100 card-hover animate-on-scroll stagger-3">
              <div className="w-full aspect-[3/4]">
                <img
                  src="https://i.imgur.com/FmT62dA.jpeg"
                  alt="405-Works Workspace"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-lg bg-gray-100 card-hover animate-on-scroll stagger-4">
              <div className="w-full aspect-[3/4]">
                <img
                  src="https://i.imgur.com/MyY2cpC.jpeg"
                  alt="405-Works Common Area"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section className="py-12 md:py-16 lg:py-24 xl:py-28 bg-[#FAF9F6]">
        <div className="max-w-[1120px] mx-auto px-5 md:px-10">
          <div className="text-center mb-10 md:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-3 md:mb-4 lg:mb-5 leading-[1.2] animate-on-scroll stagger-1">
              Amenities That Work For You
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-gray-600 max-w-[680px] mx-auto leading-[1.65] animate-on-scroll stagger-2">
              405-Works is designed to help you show up and focus on what matters most — your work. That's why we've taken care of the details, including:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
            {/* Conference Rooms */}
            <div className="bg-white rounded-xl md:rounded-2xl p-5 md:p-6 lg:p-8 card-hover animate-on-scroll stagger-1">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-4 md:mb-5">
                <i className="ri-presentation-line text-[#0A7C7C] text-3xl md:text-4xl"></i>
              </div>
              <h5 className="text-base md:text-lg lg:text-xl font-bold text-[#1A1A1A] mb-2 md:mb-3 leading-[1.25]">Conference Rooms</h5>
              <ul className="text-gray-600 text-xs md:text-sm leading-[1.65] space-y-2 mb-3 md:mb-4">
                <li>• Small Conference Room (seats up to 6)*</li>
                <li>• Large Conference Room (seats up to 24)**</li>
              </ul>
              <p className="text-xs md:text-sm text-gray-600 leading-[1.65]">Free for all 405-Works members</p>
              <p className="text-xs text-gray-500 italic mt-2">*$75/hour for non-suite members</p>
              <p className="text-xs text-gray-500 italic">**$125/hour for non-suite members</p>
            </div>

            {/* Office Essentials */}
            <div className="bg-white rounded-xl md:rounded-2xl p-5 md:p-6 lg:p-8 card-hover animate-on-scroll stagger-2">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-4 md:mb-5">
                <i className="ri-pencil-ruler-2-line text-[#0A7C7C] text-3xl md:text-4xl"></i>
              </div>
              <h5 className="text-base md:text-lg lg:text-xl font-bold text-[#1A1A1A] mb-2 md:mb-3 leading-[1.25]">Office Essentials</h5>
              <p className="text-gray-600 text-xs md:text-sm leading-[1.65]">
                Paper clips, staples, pens, tape, envelopes, paper, ink — always stocked and ready
              </p>
            </div>

            {/* Common Area */}
            <div className="bg-white rounded-xl md:rounded-2xl p-5 md:p-6 lg:p-8 card-hover animate-on-scroll stagger-3">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-4 md:mb-5">
                <i className="ri-cup-line text-[#0A7C7C] text-3xl md:text-4xl"></i>
              </div>
              <h5 className="text-base md:text-lg lg:text-xl font-bold text-[#1A1A1A] mb-2 md:mb-3 leading-[1.25]">Fully Stocked Common Area</h5>
              <p className="text-gray-600 text-xs md:text-sm leading-[1.65]">
                Coffee, snacks, and water to keep you going throughout the day
              </p>
            </div>

            {/* Receptionist */}
            <div className="bg-white rounded-xl md:rounded-2xl p-5 md:p-6 lg:p-8 card-hover animate-on-scroll stagger-1">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-4 md:mb-5">
                <i className="ri-user-smile-line text-[#0A7C7C] text-3xl md:text-4xl"></i>
              </div>
              <h5 className="text-base md:text-lg lg:text-xl font-bold text-[#1A1A1A] mb-2 md:mb-3 leading-[1.25]">On-Site Receptionist</h5>
              <p className="text-gray-600 text-xs md:text-sm leading-[1.65]">
                A friendly face to greet guests, accept packages, and help create a professional first impression
              </p>
            </div>

            {/* Cleaning */}
            <div className="bg-white rounded-xl md:rounded-2xl p-5 md:p-6 lg:p-8 card-hover animate-on-scroll stagger-2">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-4 md:mb-5">
                <i className="ri-brush-line text-[#0A7C7C] text-3xl md:text-4xl"></i>
              </div>
              <h5 className="text-base md:text-lg lg:text-xl font-bold text-[#1A1A1A] mb-2 md:mb-3 leading-[1.25]">Cleaning Services</h5>
              <p className="text-gray-600 text-xs md:text-sm leading-[1.65]">
                Professional cleaning crew keeps shared spaces and offices tidy weekly
              </p>
            </div>

            {/* Furnished Desks */}
            <div className="bg-white rounded-xl md:rounded-2xl p-5 md:p-6 lg:p-8 card-hover animate-on-scroll stagger-3">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-4 md:mb-5">
                <i className="ri-computer-line text-[#0A7C7C] text-3xl md:text-4xl"></i>
              </div>
              <h5 className="text-base md:text-lg lg:text-xl font-bold text-[#1A1A1A] mb-2 md:mb-3 leading-[1.25]">Furnished Desks</h5>
              <p className="text-gray-600 text-xs md:text-sm leading-[1.65]">
                Every private office and suite comes with a comfortable, modern workstation
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-12 md:py-16 lg:py-24 xl:py-28 bg-white">
        <div className="max-w-[1120px] mx-auto px-5 md:px-10">
          <div className="text-center mb-8 md:mb-10 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-3 md:mb-4 lg:mb-5 leading-[1.2] animate-on-scroll stagger-1">
              Conveniently Located in OKC
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-gray-600 max-w-[680px] mx-auto leading-[1.65] animate-on-scroll stagger-2">
              Located directly along the I-44 corridor, 405-Works sits at 2520 NW 39th Street in Oklahoma City. Members enjoy fast, convenient access to Penn Square Mall, The Oak, and a wide range of nearby dining, retail, and everyday services.
            </p>
          </div>

          <div className="rounded-xl md:rounded-2xl overflow-hidden shadow-lg w-full aspect-[16/9] md:aspect-[21/9] animate-on-scroll stagger-3">
            <iframe
              src="https://maps.google.com/maps?q=2520%20NW%2039th%20St%2C%20Oklahoma%20City%2C%20OK%2073112&t=m&z=15&output=embed&iwloc=near"
              className="w-full h-full border-0"
              title="405-Works Location"
              aria-label="2520 NW 39th St, Oklahoma City, OK 73112"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Inquiry Form Section */}
      <section id="inquire" className="py-16 md:py-20 lg:py-28 bg-[#1a2332]">
        <div className="max-w-[880px] mx-auto px-5 md:px-10">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4">
              Find Your Perfect Office Space
            </h2>
            <p className="text-base md:text-lg text-white/80">
              Tell us about your needs and we'll help you find the right workspace
            </p>
          </div>

          <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-12 shadow-2xl">
            {formState === 'success' ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Inquiry submitted</h3>
                <p className="text-sm md:text-base text-gray-600 mb-6">
                  Thank you for your interest. We will be in contact within 24 hours.
                </p>
                <button
                  onClick={handleFormReset}
                  className="text-[#0A7C7C] hover:text-[#0D9999] font-semibold text-sm underline cursor-pointer"
                >
                  Submit another inquiry
                </button>
              </div>
            ) : (
              <>
                <form 
                  id="inquiry-form"
                  data-readdy-form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget;

                    try {
                      setFormState('submitting');

                      const formData = new FormData(form);
                      const data: Record<string, string> = {};
                      
                      formData.forEach((value, key) => {
                        if (key === 'space-type') {
                          const select = form.querySelector(`select[name="${key}"]`) as HTMLSelectElement;
                          data[key] = select.options[select.selectedIndex].text;
                        } else {
                          data[key] = value.toString();
                        }
                      });

                      const response = await fetch('https://readdy.ai/api/form/d5km4llporqtj2nsceig', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/x-www-form-urlencoded',
                        },
                        body: new URLSearchParams(data).toString(),
                      });

                      if (response.ok) {
                        setFormState('success');
                      } else {
                        throw new Error('Submission failed');
                      }
                    } catch (error) {
                      setFormState('error');
                      setTimeout(() => {
                        setFormState('idle');
                      }, 5000);
                    }
                  }}
                >
                  {formState === 'error' && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                      <i className="ri-error-warning-line text-red-600 text-xl"></i>
                      <p className="text-red-800 text-sm font-medium">
                        Something went wrong. Please try again.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                    <div>
                      <label htmlFor="name" className="block text-xs md:text-sm font-semibold text-gray-800 mb-2">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        placeholder="Your name"
                        required
                        disabled={formState === 'submitting'}
                        className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-[#0A7C7C] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-xs md:text-sm font-semibold text-gray-800 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="your@email.com"
                        required
                        disabled={formState === 'submitting'}
                        className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-[#0A7C7C] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                    <div>
                      <label htmlFor="phone" className="block text-xs md:text-sm font-semibold text-gray-800 mb-2">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        placeholder="(405) 123-4567"
                        required
                        disabled={formState === 'submitting'}
                        className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-[#0A7C7C] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </div>

                    <div>
                      <label htmlFor="space-type" className="block text-xs md:text-sm font-semibold text-gray-800 mb-2">
                        Space Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="space-type"
                        name="space-type"
                        required
                        disabled={formState === 'submitting'}
                        className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-[#0A7C7C] focus:border-transparent cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">Select space type</option>
                        <option value="private-office">Private Office</option>
                        <option value="private-suite">Private Office Group</option>
                        <option value="conference-room">Conference Room Rental</option>
                        <option value="not-sure">Not Sure Yet</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-4 md:mb-6">
                    <label htmlFor="team-size" className="block text-xs md:text-sm font-semibold text-gray-800 mb-2">
                      Team Size
                    </label>
                    <input
                      type="text"
                      id="team-size"
                      name="team-size"
                      placeholder="e.g., Just me, 2-4 people, 5+ people"
                      disabled={formState === 'submitting'}
                      className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-[#0A7C7C] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div className="mb-4 md:mb-6">
                    <label htmlFor="details" className="block text-xs md:text-sm font-semibold text-gray-800 mb-2">
                      Additional Details
                    </label>
                    <textarea
                      id="details"
                      name="details"
                      placeholder="Tell us about your needs, preferred move-in date, or any questions you have (max 500 characters)"
                      maxLength={500}
                      rows={5}
                      disabled={formState === 'submitting'}
                      className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg text-xs md:text-sm focus:outline-none focus:ring-2 focus:ring-[#0A7C7C] focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      onChange={(e) => {
                        const counter = document.getElementById('char-counter');
                        if (counter) {
                          counter.textContent = `${e.target.value.length}/500 characters`;
                        }
                      }}
                    ></textarea>
                    <div id="char-counter" className="text-xs text-gray-500 mt-1">
                      0/500 characters
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="w-full bg-[#0A7C7C] text-white font-bold text-base md:text-lg py-3 md:py-4 rounded-lg hover:bg-[#0D9999] transition-colors duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {formState === 'submitting' ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <span>Submit Inquiry</span>
                        <i className="ri-arrow-right-line text-lg md:text-xl"></i>
                      </>
                    )}
                  </button>
                </form>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mt-8 md:mt-10 pt-8 md:pt-10 border-t border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center flex-shrink-0">
                      <i className="ri-calendar-check-line text-2xl md:text-3xl text-[#0A7C7C]"></i>
                    </div>
                    <span className="text-xs md:text-sm font-semibold text-gray-800">Flexible Terms</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl md:text-3xl text-[#0A7C7C]"></i>
                    </div>
                    <span className="text-xs md:text-sm font-semibold text-gray-800">Prime Location</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-2xl md:text-3xl text-[#0A7C7C]"></i>
                    </div>
                    <span className="text-xs md:text-sm font-semibold text-gray-800">Quick Response</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-12 md:py-16 lg:py-20">
        <div className="max-w-[1120px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 lg:gap-12 mb-8 md:mb-10 lg:mb-12">
            {/* Brand Column */}
            <div>
              <div className="mb-5 md:mb-6">
                <img
                  src="https://static.readdy.ai/image/a4fec89bcc5ba166ddea2bf47ff2dc5f/e977f47ab61e1eb868be39c5ee14b384.webp"
                  alt="405-Works"
                  className="h-10 md:h-12 w-auto"
                />
              </div>
              <p className="text-gray-400 text-xs md:text-sm lg:text-base mb-4 md:mb-5 lg:mb-6 leading-[1.65]">Modern Co-Working in the Heart of Oklahoma City</p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm md:text-base lg:text-lg font-bold mb-4 md:mb-5 lg:mb-6">Quick Links</h4>
              <ul className="space-y-2 md:space-y-3 lg:space-y-4">
                <li>
                  <a href="https://405-works.com" className="text-gray-400 text-xs md:text-sm lg:text-base hover:text-white hover:underline transition-colors inline-block py-1">
                    Home
                  </a>
                </li>
                <li>
                  <a href="https://405-works.com/?page_id=369" className="text-gray-400 text-xs md:text-sm lg:text-base hover:text-white hover:underline transition-colors inline-block py-1">
                    Co-Working Spaces
                  </a>
                </li>
                <li>
                  <a href="https://405-works.com/?page_id=365" className="text-gray-400 text-xs md:text-sm lg:text-base hover:text-white hover:underline transition-colors inline-block py-1">
                    Contact Information
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-sm md:text-base lg:text-lg font-bold mb-4 md:mb-5 lg:mb-6">Contact</h4>
              <ul className="space-y-1 md:space-y-2 lg:space-y-3 text-gray-400 text-xs md:text-sm lg:text-base leading-[1.65]">
                <li>2520 NW 39th St</li>
                <li>Oklahoma City, OK 73112</li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-6 md:pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4">
            <p className="text-gray-500 text-xs md:text-sm text-center">
              © 2025 405-Works. All rights reserved.
            </p>
            <div className="flex gap-3 md:gap-4 text-xs md:text-sm">
              <a href="https://405-works.com/?page_id=3" className="text-gray-500 hover:text-white hover:underline transition-colors py-1">
                Privacy Policy
              </a>
              <span className="text-gray-700">|</span>
              <a href="https://405-works.com/?page_id=15" className="text-gray-500 hover:text-white hover:underline transition-colors py-1">
                Terms &amp; Conditions
              </a>
            </div>
          </div>

          {/* Quick Link */}
          <div className="mt-6 md:mt-8 text-center">
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-600 text-xs hover:text-gray-400 transition-colors inline-block py-1"
            >
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
